import React, { ComponentProps } from 'react';
import { Box, Flex, Icon, Text } from '@fullstory/components';
import {
  ChangePill,
  SegmentDot,
  TimeDisplay,
  changePercentageDisplayToFormatNumberOptions,
} from '@fullstory/library-ui';
import { Display } from '@fullstory/proto/fs/services/webber/frontend/display';
import { FormattedMessage } from 'react-intl';
import { Point } from '../../util/types';
import { getTooltipDisplayValue } from './utils';
import { SegmentDotProps } from '@fullstory/library-ui/components/SegmentDot';
import { PartialPeriodWarning } from './PartialPeriodWarning';
import { Color } from '@fullstory/proto/fs/services/search-settings/visualizationsettings';

export const testIds = {
  CONTAINER: 'comparison-trends-tooltip-container',
  TIME_DISPLAY: 'comparison-trends-tooltip-time-display',
};

export interface ComparisonTrendsTooltipProps {
  currentData: Point;
  prevData: Point | null;
  groupName: string;
  display: Display;
  legendColor?: ComponentProps<typeof SegmentDot>['color'];
  comparisonRangeFirst?: boolean;
  alwaysShowTime?: boolean;
  showPartialPeriods?: boolean;
  comparisonChangeColor?: Color;
}
export const ComparisonTrendsTooltip: React.FC<ComparisonTrendsTooltipProps> = ({
  currentData,
  prevData,
  legendColor,
  groupName,
  display,
  comparisonRangeFirst,
  alwaysShowTime = false,
  showPartialPeriods = false,
  comparisonChangeColor,
}) => {
  const showPartialPeriodWarning =
    showPartialPeriods && (!!currentData.isPartialPeriod || !!prevData?.isPartialPeriod);

  return (
    <Box
      data-component="ComparisonTrendsTooltip"
      margin={12}
      width={300}
      testId={testIds.CONTAINER}
    >
      <Text appearance="bodySmall" color="default-inverse">
        {groupName}
      </Text>
      <Box margin={[16, 0, 16, 0]} testId={testIds.TIME_DISPLAY}>
        <ComparisonRow
          point={currentData}
          legendColor={legendColor}
          alwaysShowTime={alwaysShowTime}
          variant={comparisonRangeFirst ? 'secondary' : 'primary'}
          showPartialPeriods={showPartialPeriodWarning}
        />
        {prevData && (
          <Box marginTop={4}>
            <ComparisonRow
              point={prevData}
              legendColor={legendColor}
              alwaysShowTime={alwaysShowTime}
              variant={comparisonRangeFirst ? 'primary' : 'secondary'}
              showPartialPeriods={showPartialPeriodWarning}
            />
          </Box>
        )}
      </Box>
      {prevData && (
        <Flex justifyContent="space-between">
          <Text appearance="bodySmall" color="default-inverse" tag="span">
            <FormattedMessage
              defaultMessage="Change from {comparisonRangeFirst, select, true {current period} false {previous period} other {}}"
              id="Fola4H"
              values={{ comparisonRangeFirst: Boolean(comparisonRangeFirst) }}
            />
          </Text>
          {/* Pill has a marginRight of 8px, which we need to counteract */}
          <Box style={{ marginRight: -8 }}>
            <ChangePill
              value={currentData.value}
              prevValue={prevData.value}
              numberFormatOptions={changePercentageDisplayToFormatNumberOptions(display)}
              color={comparisonChangeColor}
            />
          </Box>
        </Flex>
      )}
      {showPartialPeriodWarning && <PartialPeriodWarning padding={[4, 0]} />}
    </Box>
  );
};

type ComparisonRowProps = Pick<ComparisonTrendsTooltipProps, 'legendColor' | 'alwaysShowTime'> &
  Pick<SegmentDotProps, 'variant'> & {
    point: Point;
    showPartialPeriods: boolean;
  };

function ComparisonRow({
  point,
  legendColor,
  variant,
  alwaysShowTime,
  showPartialPeriods,
}: ComparisonRowProps) {
  return (
    <Flex
      data-component="ComparisonRow"
      justifyContent="space-between"
      alignItems="flex-start"
      gap={16}
    >
      <Flex justifyContent="flex-start">
        <SegmentDot color={legendColor} variant={variant} />
        <Text appearance="bodySmallBold" color="default-inverse" tag="span">
          <TimeDisplay
            startDate={point.displayStartDate ?? point.startDate}
            endDate={point.displayEndDate ?? point.endDate}
            alwaysShowTime={alwaysShowTime}
          />
        </Text>
      </Flex>
      <Box display="flex" justifyContent="flex-end">
        <Text appearance="bodySmallBold" color="default-inverse" tag="span">
          {getTooltipDisplayValue(point)}
        </Text>
        {showPartialPeriods && (
          // want to preserve the space even without an icon
          <Box display="flex" justifyContent="flex-end" width={20}>
            {point.isPartialPeriod && <Icon size={16} name="warning-fill" color="warning" />}
          </Box>
        )}
      </Box>
    </Flex>
  );
}
